import React, { useState, useEffect, useRef } from "react";
import ModalWrapper from "Components/Modal/ModalWrapper";
import axios from "axios";
import Swal from "sweetalert2";

const ReplyModal = ({ isOpen, onClose, issue, onStatusUpdate }) => {
  const [replyMessage, setReplyMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(issue?.status || "Pending");
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && issue?._id) {
      fetchMessages();
      setStatus(issue.status || "Pending");
    }
  }, [isOpen, issue]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    if (!issue?._id) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user-issue/get-messages-by-id/${issue._id}?userType=admin`
      );
      if (res.data.success) setMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    const newMsg = {
      sender: "admin",
      text: replyMessage.trim(),
      createdAt: new Date().toISOString(),
    };

    try {
      setSending(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/user-issue/chat`, {
        issueId: issue._id,
        sender: "admin",
        text: replyMessage.trim(),
      });
      setMessages((prev) => [...prev, newMsg]);
      setReplyMessage("");
    } catch (error) {
      console.error("Error sending reply:", error);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus.toLowerCase()} this issue?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F54EA2",
      cancelButtonColor: "#aaa",
      confirmButtonText: `Yes, ${newStatus}`,
    });

    if (result.isConfirmed) {
      try {
        setStatus(newStatus);
        await axios.put(
          `${process.env.REACT_APP_API_URL}/user-issue/update-status/${issue._id}`,
          { status: newStatus }
        );
        Swal.fire("Updated!", `Issue marked as ${newStatus}.`, "success");
        fetchMessages();
      } catch (error) {
        console.error("Error updating status:", error);
        Swal.fire("Error!", "Failed to update status.", "error");
      }
    }
  };

  const handleCloseModal = () => {
    onClose();
    onStatusUpdate && onStatusUpdate();
  };

  const isClosed = status === "Resolved" || status === "Rejected";

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleCloseModal}>
      <div className="flex h-[85vh] w-full max-w-[95vw] flex-col overflow-hidden rounded-2xl bg-white p-3 shadow-xl sm:max-w-4xl sm:p-5 md:p-6">
        {/* Header */}
        <div className="relative mb-3 w-full overflow-hidden rounded-2xl border border-white/40 bg-white/30 shadow-lg backdrop-blur-md sm:mb-4">
          <div className="via-rose-50 relative bg-gradient-to-r from-pink-50 to-orange-50 p-3 sm:p-4">
            {/* Mobile Layout - Show only on small screens */}
            <div className="sm:hidden">
              {/* Top Row: Avatar + Name/Phone + Status */}
              <div className="flex items-start justify-between gap-3">
                {/* Left: Avatar + Name/Phone */}
                <div className="flex items-start gap-2">
                  <div className="to-rose-500 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 text-base font-bold text-white shadow-md">
                    {issue?.userId?.fullName?.[0] || "U"}
                  </div>
                  <div className="flex flex-col">
                    <h2 className="text-sm font-semibold text-gray-800">
                      {issue?.userId?.fullName || "User"}
                    </h2>
                    <p className="text-xs text-gray-600">
                      📞 {issue?.userId?.phone?.slice(2) || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Right: Current Status Badge */}
                <div className="flex flex-col items-start">
                  <p className=" text-[10px] uppercase tracking-wider text-gray-500">
                    Status
                  </p>
                  <div
                    className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-medium shadow-sm ${
                      status === "Resolved"
                        ? "border border-green-300 bg-green-100 text-green-700"
                        : status === "Rejected"
                        ? "border border-red-300 bg-red-100 text-red-700"
                        : "to-rose-400 bg-gradient-to-r from-pink-400 text-white"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        status === "Resolved"
                          ? "bg-green-500"
                          : status === "Rejected"
                          ? "bg-red-500"
                          : "bg-white"
                      }`}
                    ></span>
                    {status}
                  </div>
                </div>
              </div>

              {/* Status Action Buttons - Below phone */}
              {!isClosed && (
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => handleStatusChange("Resolved")}
                    className="flex-1 rounded-full bg-gradient-to-r from-pink-400 to-orange-400 px-3 py-1.5 text-xs font-medium text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  >
                    Resolve
                  </button>
                  <button
                    onClick={() => handleStatusChange("Rejected")}
                    className="flex-1 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 px-3 py-1.5 text-xs font-medium text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  >
                    Reject
                  </button>
                </div>
              )}

              {/* Subject and Description - Separate div below */}
              <div className="mt-3 flex flex-col rounded-2xl border border-white/60 bg-white/70 p-2.5 text-center shadow-inner backdrop-blur-sm">
                <h3 className="text-xs font-semibold text-gray-800">
                  {issue?.subject || "No Subject"}
                </h3>
                <p className="mt-1 text-xs leading-snug text-gray-600">
                  {issue?.description || "No description provided"}
                </p>
              </div>
            </div>

            {/* Desktop/Tablet Layout - Hidden on small screens */}
            <div className="hidden sm:block">
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-4">
                {/* Left - user info */}
                <div className="flex w-full flex-col items-center gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
                  <div className="to-rose-500 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 text-lg font-bold text-white shadow-md">
                    {issue?.userId?.fullName?.[0] || "U"}
                  </div>
                  <div className="text-center sm:text-left">
                    <h2 className="text-base font-semibold text-gray-800 md:text-lg">
                      {issue?.userId?.fullName || "User"}
                    </h2>
                    <p className="break-words text-sm text-gray-600">
                      📞 {issue?.userId?.phone?.slice(2) || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Right - status */}
                <div className="flex w-full flex-col items-center sm:w-auto sm:items-end">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Current Status
                  </p>
                  <div
                    className={`mt-1 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium shadow-sm ${
                      status === "Resolved"
                        ? "border border-green-300 bg-green-100 text-green-700"
                        : status === "Rejected"
                        ? "border border-red-300 bg-red-100 text-red-700"
                        : "via-rose-400 bg-gradient-to-r from-pink-400 to-orange-400 text-white"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        status === "Resolved"
                          ? "bg-green-500"
                          : status === "Rejected"
                          ? "bg-red-500"
                          : "bg-white"
                      }`}
                    ></span>
                    {status}
                  </div>

                  {!isClosed && (
                    <div className="mt-3 flex w-full flex-col justify-center gap-2 sm:flex-row sm:gap-2">
                      <button
                        onClick={() => handleStatusChange("Resolved")}
                        className="w-full rounded-full bg-gradient-to-r from-pink-400 to-orange-400 px-4 py-1.5 text-sm font-medium text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg sm:w-auto"
                      >
                        Mark as Resolved
                      </button>
                      <button
                        onClick={() => handleStatusChange("Rejected")}
                        className="w-full rounded-full bg-gradient-to-r from-orange-400 to-pink-500 px-4 py-1.5 text-sm font-medium text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-lg sm:w-auto"
                      >
                        Mark as Rejected
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Subject - Desktop */}
              <div className="mt-4 rounded-2xl border border-white/60 bg-white/70 p-3 text-center shadow-inner backdrop-blur-sm md:p-4">
                <h3 className="text-sm font-semibold text-gray-800 md:text-base">
                  {issue?.subject || "No Subject"}
                </h3>
                <p className="mt-1 text-sm leading-snug text-gray-600">
                  {issue?.description || "No description provided"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Section - Takes 60% height on mobile */}
        <div className="flex-1 overflow-y-auto rounded-lg bg-gray-100 p-2 sm:p-3 md:p-4">
          {loading ? (
            <p className="mt-10 text-center text-sm text-gray-500 sm:text-base">
              Loading messages...
            </p>
          ) : messages.length > 0 ? (
            messages.map((msg, i) => (
              <div
                key={i}
                className={`my-2 flex ${
                  msg.sender === "admin" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs shadow-sm sm:max-w-[80%] sm:px-4 sm:text-sm md:max-w-[70%] ${
                    msg.sender === "admin"
                      ? "bg-gradient-to-br from-pink-400 to-red-400 text-white"
                      : "border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-100 text-gray-800"
                  }`}
                >
                  <p className="break-words leading-relaxed">{msg.text}</p>
                  <p className="mt-1 text-right text-[9px] opacity-70 sm:text-[10px]">
                    {new Date(msg.createdAt).toLocaleString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="mt-10 text-center text-sm text-gray-500 sm:text-base">
              No messages yet 💬
            </p>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Reply Input */}
        {!isClosed && (
          <div className="mt-3 flex flex-col gap-2 sm:mt-4 sm:flex-row sm:items-center sm:gap-2">
            <input
              type="text"
              className="w-full flex-1 rounded-full border border-gray-300 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-pink-400 sm:px-4 sm:text-sm md:text-base"
              placeholder="Type your reply..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendReply()}
            />
            <button
              onClick={handleSendReply}
              disabled={sending}
              className={`w-full rounded-full px-4 py-2 text-xs text-white transition-all sm:w-auto sm:px-5 sm:text-sm md:text-base ${
                sending
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-gradient-to-r from-pink-400 to-pink-600 hover:scale-105"
              }`}
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default ReplyModal;
